import { GraphQLQuery, GraphQLResult } from '@aws-amplify/api';
import { API, graphqlOperation } from 'aws-amplify';
import toast from 'react-hot-toast';
import {
  CreateFormMutation,
  FormSubmission,
  ProcessModule,
  TeamModuleConfigurationInput,
  UpdateFormMutation,
} from 'src/API';
import FieldsApi from 'src/api/fields';
import { DeleteFileInput, createFormSubmission, processModuleApi } from 'src/api/process-module';
import { createForm as createFormMutation, updateForm as updateFormMutation } from 'src/graphql/mutations';
import $log from 'src/log/log';
import { slice } from 'src/slices/process-module';
import { slice as teamSlice } from 'src/slices/team';
import { AppThunk } from 'src/store';
import { FileUploadInput } from '../api/process-module/index';
import { handleThunkError } from 'src/utils/auth';

const getProcessModules =
  (): AppThunk =>
  async (dispatch): Promise<void> => {
    try {
      const modules = await processModuleApi.getProcessModules();
      if (!modules) return;
      dispatch(slice.actions.getProcessModules(modules.filter((d) => Boolean(d)) as ProcessModule[]));
    } catch (error) {
      if (handleThunkError(error)) {
        // 401 error was handled, don't continue
        return;
      }
      // Handle other errors as needed
      console.error('Error fetching process modules:', error);
    }
  };

const updateProcessModuleConfigurationForTeam =
  (teamId: string, config: TeamModuleConfigurationInput[]): AppThunk =>
  async (dispatch): Promise<void> => {
    const result = await processModuleApi.updateModuleConfigurationForTeam(teamId, config);
    if (!result) return;
    dispatch(teamSlice.actions.updateTeamModuleConfiguration(config));
  };

const getFormsForProcessModule =
  (processModuleID: string): AppThunk =>
  async (dispatch): Promise<void> => {
    try {
      const forms = await processModuleApi.getFormsForProcessModule();
      if (!forms) return;
      dispatch(slice.actions.getFormsForProcessModule(forms));
    } catch (error) {
      if (handleThunkError(error)) {
        // 401 error was handled, don't continue
        return;
      }
      // Handle other errors as needed
      console.error('Error fetching forms for process module:', error);
    }
  };

const getFields =
  (): AppThunk =>
  async (dispatch): Promise<void> => {
    try {
      const fields = await processModuleApi.getFields();
      if (!fields) {
        return;
      }
      dispatch(slice.actions.getFields(fields));
    } catch (error) {
      if (handleThunkError(error)) {
        // 401 error was handled, don't continue
        return;
      }
      // Handle other errors as needed
      console.error('Error fetching fields:', error);
    }
  };

const createFormSubmissionFunction =
  (teamId: string, input: createFormSubmission): AppThunk =>
  async (dispatch): Promise<void> => {
    const createResult = await processModuleApi.createFormSubmission(teamId, input);
    if (!createResult || !createResult.ok) {
      toast.error('Failed submitting form');
      return;
    }
    // toast.success('Updated successfully')
    dispatch(slice.actions.updateFormSubmissions([createResult.result] as FormSubmission[]));
  };

const getFormSubmissionByProcessModuleId =
  (processModuleID: string): AppThunk =>
  async (dispatch): Promise<void> => {
    const formSubmissions = await processModuleApi.getFormSubmissionByProcessModuleId(processModuleID);
    if (!formSubmissions.data?.formSubmissionsByProcessModule?.items) {
      $log.debug('no form submissions found');
      return;
    }
    dispatch(
      slice.actions.updateFormSubmissions(
        formSubmissions.data?.formSubmissionsByProcessModule?.items as FormSubmission[],
      ),
    );
  };

const uploadFile =
  (teamId: string, input: FileUploadInput, onProgress: (progressEvent: ProgressEvent) => void): AppThunk =>
  async (dispatch): Promise<void> => {
    const fileResult = await processModuleApi.createFile(teamId, input, onProgress);
    if (fileResult.ok) {
      toast.success('Uploaded successfully');
    } else {
      toast.error('Upload failed');
    }
    dispatch(slice.actions.updateFormSubmissions([fileResult.result] as FormSubmission[]));
  };

const deleteFile =
  (deleteFileInput: DeleteFileInput): AppThunk =>
  async (dispatch): Promise<void> => {
    const deleteResult = await processModuleApi.deleteFile(deleteFileInput);
    if (deleteResult.ok) {
      toast.success('Deleted successfully');
    } else {
      toast.error('Delete failed');
    }
  };

const createForm =
  (input: { name: string; description?: string | null; fields: string; processModuleID: string }): AppThunk =>
  async (dispatch): Promise<void> => {
    try {
      const result = (await API.graphql(graphqlOperation(createFormMutation, { input }))) as GraphQLResult<
        GraphQLQuery<CreateFormMutation>
      >;
      const newForm = result.data?.createForm;
      if (!newForm) {
        toast.error('Failed to create form');
        return;
      }

      // Refresh the forms list
      dispatch(getFormsForProcessModule(input.processModuleID));
      toast.success('Form created successfully');
    } catch (error) {
      console.error('Error creating form:', error);
      toast.error('Failed to create form');
    }
  };

const updateForm =
  (input: { id: string; name: string; description?: string | null; fields: string }): AppThunk =>
  async (dispatch): Promise<void> => {
    try {
      const result = (await API.graphql(graphqlOperation(updateFormMutation, { input }))) as GraphQLResult<
        GraphQLQuery<UpdateFormMutation>
      >;
      console.log('result', result);
      if (!result.data) {
        toast.error('Failed to update form');
        return;
      }
      const updatedForm = result.data?.updateForm;
      if (!updatedForm) {
        toast.error('Failed to update form');
        return;
      }

      // Refresh the forms list
      dispatch(getFormsForProcessModule(updatedForm.processModuleID));
      toast.success('Form updated successfully');
    } catch (error) {
      console.error('Error updating form:', error);
      toast.error('Failed to update form');
    }
  };

const updateField =
  (input: {
    id: string;
    name: string;
    type: string;
    required?: boolean;
    options?: string;
    organizationId: string;
  }): AppThunk =>
  async (dispatch): Promise<void> => {
    try {
      const fieldsApi = new FieldsApi();
      const result = await fieldsApi.updateField(
        input.id,
        {
          name: input.name,
          type: input.type,
          required: input.required,
          dataSource: input.options,
        },
        input.organizationId,
      );

      if (!result) {
        toast.error('Failed to update field');
        return;
      }

      dispatch(slice.actions.updateField(result));
      toast.success('Field updated successfully');
    } catch (error) {
      console.error('Error updating field:', error);
      toast.error('Failed to update field');
      throw error; // Re-throw to allow components to handle the error if needed
    }
  };

const deleteField =
  (id: string, organizationId: string): AppThunk =>
  async (dispatch): Promise<void> => {
    try {
      const fieldsApi = new FieldsApi();
      const result = await fieldsApi.deleteField(id, organizationId);

      if (!result) {
        toast.error('Failed to delete field');
        return;
      }

      dispatch(slice.actions.deleteField(id));
      toast.success('Field deleted successfully');
    } catch (error) {
      console.error('Error deleting field:', error);
      toast.error('Failed to delete field');
      throw error; // Re-throw to allow components to handle the error if needed
    }
  };

export const thunks = {
  getProcessModules,
  updateProcessModuleConfigurationForTeam,
  getFormsForProcessModule,
  getFields,
  createFormSubmission: createFormSubmissionFunction,
  getFormSubmissionByProcessModuleId,
  uploadFile,
  deleteFile,
  createForm,
  updateForm,
  updateField,
  deleteField,
};
